// Shared ⌘K command-palette logic: ranking, the keystroke cache, and the row markup.
// Two palettes use it — the partner navbar search (partner_navigation_search.mjs) and the
// admin command palette (admin_command_search_controller.js) — so everything here is
// configured by its caller rather than knowing about either one.

export const MIN_REMOTE_QUERY_LENGTH = 2;
export const MAX_RESULTS = 12;
// Short enough that results land within a keystroke of the user stopping, long
// enough that a fast typist does not fire a request per character.
export const REMOTE_SEARCH_DEBOUNCE_MS = 90;
export const RECORD_CACHE_LIMIT = 50;
// Endpoints only match substrings from three characters up (below that they match
// exactly or by prefix), so a two-character miss says nothing about longer queries
// and cannot be extended locally.
export const EMPTY_PREFIX_MIN_LENGTH = 3;

export function isCommandSearchShortcut(event) {
  return event.key?.toLowerCase() === "k" &&
    (event.metaKey || event.ctrlKey) &&
    !event.altKey;
}

export function platformModifierLabel(platform = "") {
  return /Mac|iPhone|iPad|iPod/i.test(platform) ? "⌘" : "Ctrl";
}

export function normalizeTransientNavigationState({ overlay, body, previousBodyOverflow }) {
  if (overlay) overlay.hidden = true;

  if (body && previousBodyOverflow !== undefined) body.style.overflow = previousBodyOverflow;
}

// Ranks local shortcuts (which ship with the page and have no server pass) together with
// the records an endpoint returned.
//
// recordsAreCurrent says whether the records were matched by the endpoint against this
// exact query. Records left over from a previous query must match locally to stay on
// screen, so a keystroke cannot leave an unrelated record selectable while the next
// request is in flight.
//
// localTypes are the types that must match the query literally; blankQueryTypes are the
// ones listed before anything is typed; uncappedTypes are exempt from maxResults (they
// spend the budget rather than adding to it, so the result count still holds).
export function rankCommandResults(shortcuts, records, query, {
  recordsAreCurrent = true,
  localTypes = [],
  blankQueryTypes = [],
  typeOrder = [],
  uncappedTypes = [],
  maxResults = MAX_RESULTS,
} = {}) {
  const normalizedQuery = String(query || "").trim().toLowerCase();
  const candidates = normalizedQuery ? [...shortcuts, ...records] : [...shortcuts];
  const orderOf = (type) => {
    const index = typeOrder.indexOf(type);
    return index === -1 ? typeOrder.length : index;
  };

  const scored = candidates
    .map((result) => ({ result, score: resultScore(result, normalizedQuery, recordsAreCurrent, localTypes, blankQueryTypes) }))
    .filter(({ score }) => score !== null)
    .sort((left, right) => left.score - right.score || orderOf(left.result.type) - orderOf(right.result.type));

  const budget = Math.max(maxResults - scored.filter(({ result }) => uncappedTypes.includes(result.type)).length, 0);
  let kept = 0;
  const ranked = scored
    .filter(({ result }) => uncappedTypes.includes(result.type) || kept++ < budget)
    .map(({ result }) => result);

  // The dialog renders a heading whenever the type changes, so keep each type contiguous
  // (in order of its best-scoring member) rather than emitting "Claims" twice.
  return groupByType(ranked);
}

function groupByType(results) {
  const groups = new Map();

  results.forEach((result) => {
    const group = groups.get(result.type);
    group ? group.push(result) : groups.set(result.type, [result]);
  });

  return [...groups.values()].flat();
}

function resultScore(result, query, recordsAreCurrent, localTypes, blankQueryTypes) {
  const isLocalShortcut = localTypes.includes(result.type);
  if (!query) return blankQueryTypes.includes(result.type) ? 100 : null;

  const title = String(result.title || "").toLowerCase();
  const keywords = String(result.keywords || "").toLowerCase();
  const meta = String(result.meta || "").toLowerCase();

  if (title === query) return 0;
  if (title.startsWith(query)) return 10;
  if (title.includes(query)) return 20;
  if (keywords.split(/\s+/).some((keyword) => keyword.startsWith(query))) return 30;
  if (keywords.includes(query)) return 40;
  if (meta.includes(query)) return 50;

  // The endpoint already decided this record matches, and it matches on things the
  // payload does not carry: member name words in any order, a member's employer, a
  // patient name behind a differently named bill owner. Dropping those would hide results
  // the server went to the trouble of finding, so records rank last rather than
  // disappear. Local shortcuts have no such server pass and must match here, and neither
  // do records the server matched against some earlier query.
  return isLocalShortcut || !recordsAreCurrent ? null : 60;
}

// Keystroke-level cache of the record payloads an endpoint returned. Backspacing,
// retyping, and typing past a query that matched nothing all resolve locally, so the
// only keystrokes that wait on the network are ones that can produce new records.
export class NavigationRecordCache {
  // caseSensitive keeps `GRT-1` and `grt-1` as separate entries, for a search whose
  // exact-match dimensions compare case-sensitively (the admin palette's guarantor id).
  constructor(limit = RECORD_CACHE_LIMIT, { caseSensitive = false } = {}) {
    this.limit = limit;
    this.caseSensitive = caseSensitive;
    this.entries = new Map();
  }

  key(query) {
    const trimmed = String(query || "").trim();
    return this.caseSensitive ? trimmed : trimmed.toLowerCase();
  }

  get(query) {
    return this.entries.get(this.key(query));
  }

  set(query, records) {
    const key = this.key(query);
    this.entries.delete(key);
    this.entries.set(key, records);
    if (this.entries.size > this.limit) this.entries.delete(this.entries.keys().next().value);
  }

  // Substring matching is monotonic — every record matching a longer query also
  // matches its prefixes — so a prefix that matched nothing guarantees this query
  // matches nothing either. A member phone lookup is not monotonic: it only runs on a
  // whole number (MemberPhoneSearch), so `818486594` can match nothing while
  // `8184865946` finds the caller. Phone-shaped queries therefore never infer from a
  // prefix.
  hasEmptyPrefix(query) {
    const key = this.key(query);
    if (couldBecomePhoneLookup(key)) return false;

    return [...this.entries].some(([cachedKey, records]) => (
      records.length === 0 && cachedKey.length >= EMPTY_PREFIX_MIN_LENGTH && key.startsWith(cachedKey)
    ));
  }
}

// Mirrors MemberPhoneSearch.digits: only phone punctuation, and at most the eleven digits
// of a US number with its country code. Anything longer can never become a phone lookup.
const PHONE_SHAPED = /^[\d\s().+-]+$/;
const MAX_PHONE_DIGITS = 11;

function couldBecomePhoneLookup(query) {
  return PHONE_SHAPED.test(query) && query.replace(/\D/g, "").length <= MAX_PHONE_DIGITS;
}

export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[character]
  ));
}

// Escapes everything, then wraps the typed substring in <b> so the results container's
// [&_b]: styling can tint the match.
export function highlightMatch(value, query) {
  const text = String(value ?? "");
  if (!query) return escapeHtml(text);

  const haystack = text.toLowerCase();
  let output = "";
  let index = 0;

  while (true) {
    const match = haystack.indexOf(query, index);
    if (match === -1) {
      output += escapeHtml(text.slice(index));
      break;
    }

    output += escapeHtml(text.slice(index, match)) +
      `<b>${escapeHtml(text.slice(match, match + query.length))}</b>`;
    index = match + query.length;
  }

  return output;
}

// role="presentation" keeps the listbox's children valid: only the rows are options.
export function commandGroupHeader(label) {
  return `<div role="presentation" class="px-3 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-support-dark-500 first:pt-2">${label}</div>`;
}

// iconHtml is server-rendered icon markup; everything the user or the database supplied
// goes through highlightMatch()/escapeHtml().
export function commandResultRow({ result, index, query, idPrefix, iconHtml, fallbackMeta = "Open page" }) {
  // A result with no destination (an action that swaps the result list for another search
  // view) is not a link.
  const tag = result.url ? "a" : "div";
  // The results container tints every `b`, so a danger row recolors its own highlight or
  // the matched characters would lose the red.

  return `
      <${tag} ${result.url ? `href="${escapeHtml(result.url)}"` : ""} role="option" id="${idPrefix}-${index}" data-result-index="${index}" aria-selected="false" tabindex="-1"
         class="flex cursor-pointer items-center gap-3.5 rounded-md px-3 py-2.5 transition-colors hover:bg-support-light-250">
        <span data-icon-chip class="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-support-light-250 ${result.danger ? "text-alert-red-500" : "text-support-dark-600"}">${iconHtml || ""}</span>
        <span class="min-w-0">
          <span class="block truncate text-sm font-semibold ${result.danger ? "text-alert-red-500 [&_b]:!text-alert-red-500" : "text-support-dark-800"}">${highlightMatch(result.title, query)}</span>
          <span class="block truncate text-xs text-support-dark-450">${highlightMatch(result.meta || fallbackMeta, query)}</span>
        </span>
        <span class="ml-auto flex flex-none items-center gap-2.5">
          ${result.amount ? `<span class="text-[13px] font-bold tabular-nums text-support-dark-600">${escapeHtml(result.amount)}</span>` : ""}
          <span data-enter-hint class="hidden h-5 min-w-5 items-center justify-center rounded border border-support-light-300 bg-white px-1 text-[11px] leading-none text-support-dark-600">↵</span>
        </span>
      </${tag}>
    `;
}
